import { useState, useRef, useEffect } from 'react';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import ManualInviteRow from '../registrants/manual-add-user-row';

type ManualAddUserRowData = {
  email: string;
  firstName: string;
  lastName: string;
};

type ManuallyAddedUser = {
  email: string;
  first_name: string;
  last_name: string;
  passcode: string;
};

type ManualAddUserProps = {
  onChange: (users: ManuallyAddedUser[]) => void;
  onRowCountChange?: (count: number) => void;
};

const ManualAddUser = ({ onChange }: ManualAddUserProps) => {
  const [rows, setRows] = useState<ManualAddUserRowData[]>([{ email: '', firstName: '', lastName: '' }]);
  const addBtnRef = useRef<HTMLDivElement>(null);

  const notifyParent = (updatedRows: ManualAddUserRowData[]) => {
    onChange(
      updatedRows
        .filter((row) => row.email.trim())
        .map((row) => ({
          email: row.email,
          first_name: row.firstName,
          last_name: row.lastName,
          passcode: '',
        }))
    );
  };

  const shouldScrollRef = useRef(false);

  useEffect(() => {
    if (!shouldScrollRef.current) return;
    shouldScrollRef.current = false;

    addBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [rows.length]);

  const handleAddRow = () => {
    shouldScrollRef.current = true;
    const updated = [...rows, { email: '', firstName: '', lastName: '' }];
    setRows(updated);
    notifyParent(updated);
  };

  const handleRowChange = (index: number, field: keyof ManualAddUserRowData, value: string) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
    notifyParent(updated);
  };

  const handleDeleteRow = (index: number) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
    notifyParent(updated);
  };

  return (
    <Container className="flex-column gap-4 padding-v-12 padding-r-4">
      <Container className="flex ai-center gap-12">
        <Container className="flex-2">
          <Text className="L2 padding-l-12" color="var(--gray-500)">
            Email*
          </Text>
        </Container>
        <Container className="flex-1">
          <Text className="L2 padding-l-12" color="var(--gray-500)">
            First name
          </Text>
        </Container>
        <Container className="flex-1">
          <Text className="L2 padding-l-12" color="var(--gray-500)">
            Last name
          </Text>
        </Container>
        <div style={{ width: '16px', flexShrink: 0 }}></div>
      </Container>
      <Container className="flex-column gap-12" style={{ overflowX: 'hidden' }}>
        {rows.map((row, index) => (
          <ManualInviteRow
            key={index}
            email={row.email}
            firstName={row.firstName}
            lastName={row.lastName}
            onEmailChange={(value) => handleRowChange(index, 'email', value)}
            onFirstNameChange={(value) => handleRowChange(index, 'firstName', value)}
            onLastNameChange={(value) => handleRowChange(index, 'lastName', value)}
            onDelete={rows.length > 1 ? () => handleDeleteRow(index) : undefined}
          />
        ))}
      </Container>
      <div ref={addBtnRef}>
        <Button
          design={TButtonAndLinkDesigns.LocalBttnSecHalfTransp}
          width="72px"
          designSize="small"
          onClick={handleAddRow}
          className="flex ai-center jc-center gap-6 padding-0 margin-t-6"
          backgroundColor="var(--gray-700)"
        >
          <Icon name={ICONS.ADD} size={12} />
          <Text className="L2">Add</Text>
        </Button>
      </div>
    </Container>
  );
};

export default ManualAddUser;
